// Figma: no frame provided yet — built using the established design system (bordered card
// shell, same as ../Esg/EsgSection.js, with an @mui/lab Tab bar already a dependency of this
// app — see themes/ThemeCustomization.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's Glance.js (commons/components/HomeTabs/Today/Portfolio/Glance/
// Glance.js): same `getGlanceData` fetch off `params.account_sfid` (returns `{data, tabs}`,
// `data` keyed by tab name), same admin/permission + community_sfid scoping of each tab's
// rows (Stake admin/Administrator sees the selected community — or every community the
// dashboard currently scopes to when none is selected — everyone else only their
// `propertyPermissions`, further narrowed to the selected community), same `activeTabs`
// derivation (only tabs the response actually returned data for get a Tab), same 5 sub-tab
// -> component mapping (Concession Comps -> ./ConcessionGlance, Historical Days on Market ->
// ./DaysOnMarketGlance, Delinquency -> ./DelinquencyGlance [LC2's file is named
// DeinquencyGlance.js — a typo in LC2 itself; renamed here, same as LC2's own Glance.js
// importing it under the corrected name], Vacancy -> ./VacancyGlance, Exposure ->
// ./ExposureGlance).
// SIMPLIFIED: LC2 also gated on `getCommunityLoadingStatus`, a loading-slice entry this app's
// trimmed loading.js doesn't carry (see that file's header comment) — this just uses the
// `useGetGlanceDataQuery` hook's own isLoading/isFetching, which covers the same "communities
// haven't loaded yet" window in practice since the account fetch that populates communities
// resolves before this query is of any use.
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {Box, Paper, Stack, Tab, Typography} from '@mui/material';
import {TabContext, TabList, TabPanel} from '@mui/lab';

import {tokens} from '../../../themes/ThemeCustomization';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../../app/store/reducers/session';
import {getDashboardCommunities} from '../../../app/store/reducers/dashboard';
import {useGetGlanceDataQuery} from '../../../features/coreApi/metricsSlice';
import LoadingBox from '../../../commons/components/LoadingBox';

import ConcessionGlance from './ConcessionGlance';
import DaysOnMarketGlance from './DaysOnMarketGlance';
import DelinquencyGlance from './DelinquencyGlance';
import VacancyGlance from './VacancyGlance';
import ExposureGlance from './ExposureGlance';

const CARD_SX = {border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white};

const GlanceTabs = ({data, tabs}) => {
    const params = useParams();
    const [value, setValue] = useState('0');
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);
    const communities = useSelector(getDashboardCommunities);

    const {glanceData, activeTabs} = useMemo(() => {
        const newData = {};
        const newActiveTabs = [];

        if (isStakeAdmin || isAdministrator) {
            const cSfids = params?.community_sfid ? [params.community_sfid] : communities.map((c) => c.sfid);
            for (const key in data) {
                newData[key] = (data[key] || []).filter((row) => cSfids.includes(row.property_sfid));
                newActiveTabs.push(key);
            }
        } else {
            for (const key in data) {
                let filtered = (data[key] || []).filter((row) => propertyPermissions.includes(row.property_sfid));
                if (params?.community_sfid) {
                    filtered = filtered.filter((row) => row.property_sfid === params.community_sfid);
                }
                newData[key] = filtered;
                newActiveTabs.push(key);
            }
        }

        return {glanceData: newData, activeTabs: newActiveTabs};
    }, [data, isStakeAdmin, isAdministrator, propertyPermissions, communities, params]);

    const handleChange = (event, newValue) => {
        event.preventDefault();
        event.stopPropagation();
        setValue(newValue);
    };

    if (!tabs || !tabs.length || activeTabs.length === 0) {
        return null;
    }

    return (
        <Paper sx={{...CARD_SX, boxShadow: 'none'}}>
            <Stack direction="column" divider={<Box sx={{borderTop: `1px solid ${tokens.color.neutral200}`}} />}>
                <Stack direction="column" sx={{p: 2}}>
                    <Typography variant="h5" fontWeight="fontWeightMedium">
                        At a glance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        For last 3 months
                    </Typography>
                </Stack>
                <Box sx={{width: '100%'}}>
                    <TabContext value={value}>
                        <Box sx={{borderBottom: 1, borderColor: tokens.color.neutral200}}>
                            <TabList
                                onChange={handleChange}
                                aria-label="at a glance tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    '.MuiTab-root': {fontWeight: 400, textTransform: 'none'},
                                    '.Mui-selected': {color: `${tokens.color.sky600} !important`},
                                    '.MuiTabs-indicator': {backgroundColor: tokens.color.sky600}
                                }}
                            >
                                {activeTabs.map((tab, index) => (
                                    <Tab key={tab} label={tab} value={index.toString()} />
                                ))}
                            </TabList>
                        </Box>
                        {activeTabs.map((tab, index) => {
                            let tabComponent = null;

                            switch (tab) {
                                case 'Concession Comps':
                                    tabComponent = <ConcessionGlance data={glanceData['Concession Comps']} />;
                                    break;
                                case 'Historical Days on Market':
                                    tabComponent = <DaysOnMarketGlance data={glanceData['Historical Days on Market']} />;
                                    break;
                                case 'Delinquency':
                                    tabComponent = <DelinquencyGlance data={glanceData['Delinquency']} />;
                                    break;
                                case 'Vacancy':
                                    tabComponent = <VacancyGlance data={glanceData['Vacancy']} />;
                                    break;
                                case 'Exposure':
                                    tabComponent = <ExposureGlance data={glanceData['Exposure']} />;
                                    break;
                                default:
                                    break;
                            }

                            if (!tabComponent) return null;

                            return (
                                <TabPanel key={tab} value={index.toString()} sx={{p: 0}}>
                                    {tabComponent}
                                </TabPanel>
                            );
                        })}
                    </TabContext>
                </Box>
            </Stack>
        </Paper>
    );
};

const GlanceSection = () => {
    const params = useParams();
    const {data, isError, isSuccess, isLoading, isFetching} = useGetGlanceDataQuery(params?.account_sfid, {
        skip: !params?.account_sfid
    });

    if (isLoading || isFetching) {
        return (
            <Box sx={CARD_SX}>
                <LoadingBox />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{...CARD_SX, p: 2}}>
                <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                    Failed to fetch &quot;at a glance&quot; data.
                </Typography>
            </Box>
        );
    }

    if (isSuccess) {
        return <GlanceTabs data={data?.data} tabs={data?.tabs} />;
    }

    return null;
};

export default GlanceSection;
